import {
    FocusableComponentLayout,
    FocusContext,
    FocusDetails,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation';
import { useSelector } from '../../../../redux/store';
import {
    UpdateDeliveryFormData,
    UpdateDeliveryFormDataType,
} from '../../../../redux/slices/deliveryFormSlice';
import { useDispatch } from 'react-redux';
import DeliveryInputs from './DeliveryInputs';
import PickupInputs from './PickupInputs';
import ChooseTimeOfOrder from './ChooseTimeOfOrder';
import OrderNotesInput from './OrderNotesInput';
import DeliveryOrPickup from './DeliveryOrPickup';
import BackButton from '../../../../components/Buttons/BackButton';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

type DeliveryFormInputsProps = {
    focusKey: string;
    onRowFocus: (layout: FocusableComponentLayout, props: unknown, details: FocusDetails) => void;
};
const DeliveryFormInputs = ({ focusKey: focusKeyParam, onRowFocus }: DeliveryFormInputsProps) => {
    const navigate = useNavigate();
    const deliveryFormData = useSelector((state) => state.deliveryFormData.deliveryForm);
    const addresses = useSelector((state) => state.userAuth.user?.addresses);
    const branchesList = useSelector((state) => state.branches.branchesList);
    const isDeliveryOrPickup = deliveryFormData.order_type === 0 ? 'Delivery' : 'Pickup';

    const dispatch = useDispatch();
    const { ref, focusKey } = useFocusable({
        focusKey: focusKeyParam,
    });

    const handleDeliveryFormUpdation = (updates: UpdateDeliveryFormDataType) => {
        dispatch(UpdateDeliveryFormData(updates));
    };

    // Effect to auto-select first valid address and time
    useEffect(() => {
        if (
            isDeliveryOrPickup === 'Delivery' &&
            addresses &&
            addresses.length > 0 &&
            !deliveryFormData.address
        ) {
            // Check if there are valid addresses (city length > 2)
            const validAddresses = addresses.filter((address) => address.city.length > 2);
            if (validAddresses.length > 0) {
                const firstAddress = validAddresses[0];
                handleDeliveryFormUpdation({
                    address: firstAddress,
                    branch_id: firstAddress.closest_branch.external_branch_id,
                    delivery_price: parseInt(
                        firstAddress.closest_branch.properties?.delivery_charge,
                    ),
                });
            }
        }

        // Auto-select first time option when branch is selected
        if (deliveryFormData.branch_id && !deliveryFormData.future_date) {
            const currentBranchData = branchesList.find(
                (branch) => branch.external_branch_id === deliveryFormData.branch_id,
            );
            if (currentBranchData?.atmos_data) {
                // Set to current time for immediate delivery/pickup
                handleDeliveryFormUpdation({
                    future_date: new Date(),
                });
            }
        }
    }, [
        isDeliveryOrPickup,
        addresses,
        deliveryFormData.address,
        deliveryFormData.branch_id,
        branchesList,
    ]);

    return (
        <FocusContext.Provider value={focusKey}>
            <div className="flex flex-col mb-[2vw]" ref={ref}>
                <BackButton
                    className="py-[0.6vw] bg-transparent-card"
                    onClick={() => navigate('/')}
                    onBtnFocus={onRowFocus}
                />
                <DeliveryOrPickup onRowFocus={onRowFocus} focusKey="delivery-or-pickup" />
                <div className="flex flex-col">
                    {isDeliveryOrPickup === 'Delivery' ? (
                        <DeliveryInputs
                            focusKey="DELIVERY-INPUTS"
                            onRowFocus={onRowFocus}
                            handleDeliveryFormUpdation={handleDeliveryFormUpdation}
                        />
                    ) : (
                        <PickupInputs
                            focusKey="PICKUP-INPUTS"
                            onRowFocus={onRowFocus}
                            handleDeliveryFormUpdation={handleDeliveryFormUpdation}
                        />
                    )}
                    <ChooseTimeOfOrder
                        focusKey="CHOOSE-TIME-OF-ORDER"
                        handleDeliveryFormUpdation={handleDeliveryFormUpdation}
                    />
                    <OrderNotesInput
                        onRowFocus={onRowFocus}
                        focusKey="CHECKOUT-NOTE-INPUT-CONTAINER"
                    />
                </div>
            </div>
        </FocusContext.Provider>
    );
};

export default DeliveryFormInputs;
